// 같은 가구(형제자매) 판정 — 동·호 숫자가 아니라 "정확한 도로명주소"로 같은 집을 식별한다.
import type { Student } from "@/types";

/** 전화번호 정규화 — 콤마 다중번호 중 첫 번째, 숫자만 추출. */
export function normalizePhone(phone?: string): string {
  return (phone || "").split(",")[0].replace(/[^0-9]/g, "");
}

/** 주소 비교용 정규화 — 공백·콤마·괄호 제거. */
function normAddr(a?: string): string {
  return (a || "").replace(/[\s,()]/g, "");
}

/**
 * 같은 집 식별 키. 오늘 경로(RouteDay)의 묶음과 배송 상세(DeliveryDetail)의 자동 체크가 둘 다 이 키를 쓴다.
 * - 동·호가 다 있으면: 정확한 도로명주소(refinedAddress) + 건물동 + 호.
 *   동·호 숫자만 보면 "낙민동 101동 204호"처럼 다른 단지끼리 충돌하므로 도로명주소를 포함한다.
 * - 호는 있는데 동이 비면: 한 주소에 여러 동이 있는 단지에서 다른 동의 같은 호수와 섞인다(4차 점검 20).
 *   그래서 보호자 번호까지 같을 때만 같은 집으로 본다.
 * - 호수가 없으면(주택 등): 주소만으로 단정 불가 → 보호자 번호 + 법정동.
 * 키가 없으면("") 가구로 묶지 않는다.
 */
export function householdKey(
  s: Pick<Student, "refinedAddress" | "buildingDong" | "buildingHo" | "phone" | "gu" | "dong">,
): string {
  const p = normalizePhone(s.phone);
  if (s.buildingHo) {
    if (s.buildingDong) return `U|${normAddr(s.refinedAddress)}|${s.buildingDong}|${s.buildingHo}`;
    return p ? `H|${p}|${normAddr(s.refinedAddress)}|${s.buildingHo}` : "";
  }
  return p ? `P|${p}|${s.gu}|${s.dong}` : "";
}

/**
 * target 과 같은 집인 형제 후보를 분리.
 *   sameUnit  = 같은 집(같은 주소 키, 기본 체크 대상) — 엄마/아빠 번호가 달라도 묶임
 *   otherUnit = 번호는 같지만 주소가 다름(다른 집일 수 있어 경고·체크 해제)
 */
export function findHousehold(
  target: Student,
  candidates: Student[],
): { sameUnit: Student[]; otherUnit: Student[] } {
  const others = candidates.filter((s) => s.id !== target.id);
  const key = householdKey(target);
  const phone = normalizePhone(target.phone);

  const sameUnitList = key ? others.filter((s) => householdKey(s) === key) : [];
  const sameIds = new Set(sameUnitList.map((s) => s.id));
  const otherUnit = phone
    ? others.filter((s) => normalizePhone(s.phone) === phone && !sameIds.has(s.id))
    : [];

  return { sameUnit: sameUnitList, otherUnit };
}
